import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import LiveClock from '../components/LiveClock';
import RecordDetailModal from '../components/RecordDetailModal';
import MotivationalPopup from '../components/MotivationalPopup';
import RankWidget from '../components/RankWidget';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ChevronLeft, ChevronRight, Search, Eye, Target, TrendingUp, 
  TrendingDown, Calendar, Zap, Trophy, Flame, Star, ArrowUp, ArrowDown,
  BarChart3, Clock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AgentDashboard = ({ user, onLogout }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchField, setSearchField] = useState('email');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [leaderboardData, setLeaderboardData] = useState(null);
  const pageSize = 50;

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = { page, page_size: pageSize };
      if (searchTerm) {
        params.search = searchTerm;
        params.search_field = searchField;
      }
      const response = await axios.get(`${BACKEND_URL}/api/records`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setRecords(response.data.records);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch records');
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, searchField]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/stats/agent`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats');
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/leaderboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeaderboardData(response.data);
    } catch (error) {
      console.error('Failed to fetch leaderboard for motivation');
    }
  };

  useEffect(() => {
    fetchRecords();
    fetchStats();
    fetchLeaderboard();
  }, [fetchRecords]);

  const handleSearch = () => {
    setPage(1);
    fetchRecords();
  };

  const viewDetails = (record) => {
    setSelectedRecord(record);
    setDetailOpen(true);
  };

  const getGreeting = () => {
    // Get current hour in EST timezone
    const estTime = new Date().toLocaleString('en-US', { timeZone: 'America/New_York', hour: 'numeric', hour12: false });
    const hour = parseInt(estTime, 10);
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    if (hour < 20) return 'Good Evening';
    return 'Good Night';
  };

  const getMotivationalMessage = () => {
    if (!stats) return "Let's make today count!";
    const growth = stats.growth_percentage;
    if (growth > 20) return "You're on fire! Keep crushing it!";
    if (growth > 0) return "Great progress! Keep the momentum going!";
    if (growth === 0) return "Fresh start! Time to shine!";
    return "Every expert was once a beginner. Let's go!";
  };

  const getPerformanceEmoji = () => {
    if (!stats) return "🎯";
    const growth = stats.growth_percentage;
    if (growth > 50) return "🔥";
    if (growth > 20) return "🚀";
    if (growth > 0) return "⭐";
    return "💪";
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Sidebar user={user} onLogout={onLogout} />
      {/* Motivational Popup - shows once per hour */}
      <MotivationalPopup user={user} stats={stats} leaderboardData={leaderboardData} />
      <main className="flex-1 p-8 overflow-auto" data-testid="agent-dashboard">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-1">
                {getGreeting()}, <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500">{user?.agent_name || 'Agent'}</span>!
              </h1>
              <p className="text-slate-400 flex items-center gap-2">
                <span className="text-xl">{getPerformanceEmoji()}</span>
                <span>{getMotivationalMessage()}</span>
              </p>
            </div>
            <LiveClock />
          </div>

          {/* Rank Widget - Prominent leaderboard position */}
          <RankWidget user={user} />

          {/* Performance Stats Cards */}
          {!statsLoading && stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Last Week */}
              <Card className="border-0 bg-gradient-to-br from-slate-800 to-slate-800/50 backdrop-blur-sm overflow-hidden relative group hover:scale-[1.02] transition-transform">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-transparent"></div>
                <CardContent className="p-5 relative">
                  <div className="flex items-center justify-between mb-3">
                    <div className="p-2 bg-blue-500/20 rounded-lg">
                      <Calendar className="w-5 h-5 text-blue-400" />
                    </div>
                    <span className="text-xs text-slate-500 font-medium">
                      {stats.last_week.start_date} - {stats.last_week.end_date}
                    </span>
                  </div>
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Last Week</p>
                  <p className="text-3xl font-bold text-white">{stats.last_week.total.toLocaleString()}</p>
                  <p className="text-xs text-slate-500 mt-1">Records processed</p>
                </CardContent>
              </Card>

              {/* Current Week */}
              <Card className="border-0 bg-gradient-to-br from-slate-800 to-slate-800/50 backdrop-blur-sm overflow-hidden relative group hover:scale-[1.02] transition-transform">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-transparent"></div>
                <div className="absolute top-2 right-2">
                  <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs font-bold rounded-full animate-pulse">
                    LIVE
                  </span>
                </div>
                <CardContent className="p-5 relative">
                  <div className="flex items-center justify-between mb-3">
                    <div className="p-2 bg-emerald-500/20 rounded-lg">
                      <Zap className="w-5 h-5 text-emerald-400" />
                    </div>
                    <span className="text-xs text-slate-500 font-medium">
                      {stats.current_week.start_date} - {stats.current_week.end_date}
                    </span>
                  </div>
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Current Week</p>
                  <div className="flex items-end gap-2">
                    <p className="text-3xl font-bold text-white">{stats.current_week.total.toLocaleString()}</p>
                    {stats.growth_percentage !== 0 && (
                      <div className={`flex items-center gap-1 text-sm font-semibold mb-1 ${stats.growth_percentage > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {stats.growth_percentage > 0 ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                        {Math.abs(stats.growth_percentage)}%
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Day {stats.current_week.days_elapsed} of 5</p>
                </CardContent>
              </Card>

              {/* Prediction */}
              <Card className="border-0 bg-gradient-to-br from-slate-800 to-slate-800/50 backdrop-blur-sm overflow-hidden relative group hover:scale-[1.02] transition-transform">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-transparent"></div>
                <CardContent className="p-5 relative">
                  <div className="flex items-center justify-between mb-3">
                    <div className="p-2 bg-purple-500/20 rounded-lg">
                      <TrendingUp className="w-5 h-5 text-purple-400" />
                    </div>
                    <span className="text-xs text-slate-500 font-medium">
                      {stats.prediction.remaining_days > 0 ? `${stats.prediction.remaining_days} days left` : 'Week complete'}
                    </span>
                  </div>
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Week Prediction</p>
                  <p className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                    {stats.prediction.total.toLocaleString()}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Projected total (Mon-Fri)</p>
                </CardContent>
              </Card>

              {/* Daily Average */}
              <Card className="border-0 bg-gradient-to-br from-slate-800 to-slate-800/50 backdrop-blur-sm overflow-hidden relative group hover:scale-[1.02] transition-transform">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-transparent"></div>
                <CardContent className="p-5 relative">
                  <div className="flex items-center justify-between mb-3">
                    <div className="p-2 bg-amber-500/20 rounded-lg">
                      <BarChart3 className="w-5 h-5 text-amber-400" />
                    </div>
                    <div className="flex items-center gap-1">
                      {stats.daily_average > (stats.last_week.total / 5) ? (
                        <TrendingUp className="w-4 h-4 text-emerald-400" />
                      ) : (
                        <TrendingDown className="w-4 h-4 text-red-400" />
                      )}
                    </div>
                  </div>
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Daily Average</p>
                  <p className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-400">
                    {stats.daily_average}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Records per day this week</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Daily Breakdown Chart */}
          {!statsLoading && stats && (
            <Card className="border-0 bg-gradient-to-br from-slate-800 to-slate-800/50 backdrop-blur-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                  <Flame className="w-5 h-5 text-orange-400" />
                  This Week's Progress
                  <span className="text-sm font-normal text-slate-400 ml-2">
                    ({stats.current_week.start_date} - {stats.current_week.end_date})
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-2">
                <div className="flex items-end justify-between gap-3 h-40">
                  {stats.daily_breakdown.map((day, index) => {
                    const maxCount = Math.max(...stats.daily_breakdown.map(d => d.count), 1);
                    const heightPercent = (day.count / maxCount) * 100;
                    const isToday = day.is_today;
                    const isFuture = day.is_future;
                    
                    return (
                      <div key={index} className="flex-1 flex flex-col items-center gap-2">
                        <div className="w-full h-32 flex items-end justify-center">
                          <div 
                            className={`w-full max-w-[50px] rounded-t-lg transition-all duration-500 relative group ${
                              isFuture 
                                ? 'bg-slate-700/30 border-2 border-dashed border-slate-600' 
                                : isToday 
                                  ? 'bg-gradient-to-t from-amber-500 to-orange-400 shadow-lg shadow-orange-500/30' 
                                  : 'bg-gradient-to-t from-blue-600 to-blue-400'
                            }`}
                            style={{ height: isFuture ? '30%' : `${Math.max(heightPercent, 10)}%` }}
                          >
                            {!isFuture && (
                              <div className="absolute -top-8 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-slate-900 px-2 py-1 rounded text-xs text-white whitespace-nowrap">
                                {day.count} records
                              </div>
                            )}
                            {isToday && (
                              <div className="absolute -top-1 left-1/2 -translate-x-1/2">
                                <Star className="w-4 h-4 text-amber-300 fill-amber-300 animate-pulse" />
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-center">
                          <p className={`text-sm font-bold ${isToday ? 'text-amber-400' : isFuture ? 'text-slate-600' : 'text-slate-300'}`}>
                            {day.day}
                          </p>
                          <p className={`text-xs ${isFuture ? 'text-slate-600' : 'text-slate-400'}`}>
                            {isFuture ? '-' : day.count}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Legend */}
                <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-slate-700">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-gradient-to-r from-blue-600 to-blue-400"></div>
                    <span className="text-xs text-slate-400">Completed</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-gradient-to-r from-amber-500 to-orange-400"></div>
                    <span className="text-xs text-slate-400">Today</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded border-2 border-dashed border-slate-600"></div>
                    <span className="text-xs text-slate-400">Upcoming</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Search Section */}
          <Card className="border-0 bg-gradient-to-br from-slate-800 to-slate-800/50 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                <div className="md:col-span-3">
                  <Select value={searchField} onValueChange={setSearchField}>
                    <SelectTrigger data-testid="agent-search-field" className="h-12 bg-slate-700/50 border-slate-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="phone">Phone</SelectItem>
                      <SelectItem value="offer">Offer</SelectItem>
                      <SelectItem value="verification_id">Verification ID</SelectItem>
                      <SelectItem value="first_name">First Name</SelectItem>
                      <SelectItem value="last_name">Last Name</SelectItem>
                      <SelectItem value="city">City</SelectItem>
                      <SelectItem value="state">State</SelectItem>
                      <SelectItem value="agent">Agent Name</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-7">
                  <Input
                    placeholder="Search records..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="h-12 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400"
                    data-testid="agent-search-input"
                  />
                </div>
                <div className="md:col-span-2">
                  <Button 
                    onClick={handleSearch} 
                    className="w-full h-12 bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-700 hover:to-emerald-700 shadow-lg" 
                    data-testid="agent-search-button"
                  >
                    <Search className="mr-2 h-4 w-4" />
                    Search
                  </Button>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between">
                <p className="text-sm text-slate-400" data-testid="agent-total-records">
                  Total Records: <span className="font-bold text-white text-lg">{total.toLocaleString()}</span>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Data Table */}
          <Card className="border-0 bg-gradient-to-br from-slate-800 to-slate-800/50 backdrop-blur-sm">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="bg-slate-700/50">
                    <TableRow className="border-slate-600">
                      <TableHead className="font-semibold text-slate-300">Date</TableHead>
                      <TableHead className="font-semibold text-slate-300">Name</TableHead>
                      <TableHead className="font-semibold text-slate-300">Email</TableHead>
                      <TableHead className="font-semibold text-slate-300">Phone</TableHead>
                      <TableHead className="font-semibold text-slate-300">Offer</TableHead>
                      <TableHead className="font-semibold text-slate-300">Credit Score</TableHead>
                      <TableHead className="font-semibold text-slate-300">City</TableHead>
                      <TableHead className="font-semibold text-slate-300">State</TableHead>
                      <TableHead className="font-semibold text-center text-slate-300">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-slate-400">
                          Loading records...
                        </TableCell>
                      </TableRow>
                    ) : records.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-slate-400">
                          No records found
                        </TableCell>
                      </TableRow>
                    ) : (
                      records.map((record, index) => (
                        <TableRow key={record.id} className="border-slate-700 hover:bg-slate-700/50 transition-colors" data-testid={`agent-record-${index}`}>
                          <TableCell className="font-mono text-xs text-slate-300">{record.date}</TableCell>
                          <TableCell className="font-medium text-white">
                            {record.first_name} {record.last_name}
                          </TableCell>
                          <TableCell className="font-mono text-xs text-slate-300">{record.email}</TableCell>
                          <TableCell className="font-mono text-xs text-slate-300">{record.phone}</TableCell>
                          <TableCell>
                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-emerald-500/20 text-emerald-400">
                              {record.offer}
                            </span>
                          </TableCell>
                          <TableCell className="font-mono text-sm font-semibold text-white">{record.credit_score}</TableCell>
                          <TableCell className="text-slate-300">{record.city}</TableCell>
                          <TableCell className="text-slate-300">{record.state}</TableCell>
                          <TableCell className="text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => viewDetails(record)}
                              className="text-slate-400 hover:text-white hover:bg-slate-600"
                              data-testid={`view-agent-detail-${index}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-400">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  data-testid="agent-prev-page"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPages}
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  data-testid="agent-next-page"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
      <RecordDetailModal record={selectedRecord} open={detailOpen} onClose={() => setDetailOpen(false)} />
      <Footer />
    </div>
  );
};

export default AgentDashboard;
